import React from 'react'

/**
 * PreFollowupModal - Aviso anticipado "Faltan 10 minutos" para el agendamiento
 * (10 min antes, Carlos 18-09 §7; el tipo de notificación se conserva como 'pre_15min').
 *
 * Se muestra para notificaciones de tipo 'pre_15min'. A diferencia del
 * NotificationModal del momento (rojo, urgente, con Posponer), este es un
 * heads-up ámbar/informativo SIN posponer: solo "Entendido" e "Ir al contacto".
 * El posponer vive únicamente en el modal del momento exacto (NotificationModal).
 *
 * onAcknowledge: descarta el aviso localmente (no completa ni pospone el
 * seguimiento; la alerta del momento exacto saltará igual a su hora).
 * onGoToContact: navega al contacto y descarta el aviso.
 */
export default function PreFollowupModal({ notification, onGoToContact, onAcknowledge }) {
  if (!notification) return null

  const contactName = notification.first_name
    ? `${notification.first_name} ${notification.last_name || ''}`.trim()
    : notification.contacto_nombre || notification.nombre || notification.contacto_phone

  const timeStr = notification.scheduled_at?.split('T')[1]?.substring(0, 5) || ''

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Overlay (click descarta el aviso) */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => onAcknowledge && onAcknowledge(notification?.id)}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-surface-dark-alt rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden animate-slide-in-right">
        {/* Header con gradiente ámbar (informativo, no alarmante) */}
        <div className="bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 px-8 py-7 relative">
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 bg-white/25 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <svg className="w-9 h-9 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <div className="text-sm font-bold uppercase tracking-wider text-amber-50">Próximo seguimiento</div>
              <div className="text-4xl font-bold text-white">Faltan 10 min</div>
            </div>
            <button
              onClick={() => onAcknowledge && onAcknowledge(notification?.id)}
              className="text-white/80 hover:text-white text-3xl leading-none p-1"
              title="Entendido"
            >
              &times;
            </button>
          </div>
        </div>

        {/* Body - info del prospecto */}
        <div className="px-8 py-6 space-y-4">
          <div>
            <div className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
              Contactar a las {timeStr || '—'}
            </div>
            <div className="text-3xl font-bold text-gray-900 dark:text-gray-100 leading-tight">
              {contactName}
            </div>
            <div className="text-lg text-gray-500 dark:text-gray-400 font-medium">
              {notification.contacto_phone}
            </div>
          </div>

          {notification.hint && (
            <div className="border-l-4 border-amber-400 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 rounded-r-lg">
              <div className="text-sm font-semibold uppercase text-amber-700 dark:text-amber-300 mb-0.5">Pista</div>
              <div className="text-lg text-gray-800 dark:text-gray-200">{notification.hint}</div>
            </div>
          )}
        </div>

        {/* Footer - botones (sin Posponer) */}
        <div className="px-8 pb-8 pt-3 flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => onAcknowledge && onAcknowledge(notification?.id)}
            className="flex-1 px-4 py-4 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl text-lg font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition flex items-center justify-center gap-2"
          >
            Entendido
          </button>
          <button
            onClick={() => onGoToContact && onGoToContact(notification)}
            className="flex-1 px-6 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-xl text-lg font-semibold hover:from-amber-600 hover:to-amber-700 transition shadow-md flex items-center justify-center gap-2"
          >
            Ir al contacto
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}
